import { INVENTORY_V2_FILTER_KEYS } from '../models/inventory-v2';
import type { InventoryV2FilterParams, InventoryV2FilterValues } from '../models/inventory-v2';

const FILTERS_PARAM = 'filters';
const KNOWN_KEYS = new Set<string>(INVENTORY_V2_FILTER_KEYS);

const isFilled = (value: unknown) =>
  typeof value === 'string' ? value !== '' : Array.isArray(value) && value.length > 0;

/**
 * Reads the `?filters=<json>` search param used by the current inventory, keeping only keys
 * that `/assets/list/partial` understands. Malformed JSON yields no filters.
 */
export const parseFilterValues = (search: string): InventoryV2FilterValues => {
  const raw = new URLSearchParams(search).get(FILTERS_PARAM);
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([key, value]) =>
          KNOWN_KEYS.has(key) &&
          isFilled(value) &&
          (typeof value === 'string' || value.every((v: unknown) => typeof v === 'string'))
      )
    ) as InventoryV2FilterValues;
  } catch {
    return {};
  }
};

/**
 * Maps the selection to `/assets/list/partial` query params: one comma-joined value per key.
 */
export const toFilterParams = (values: InventoryV2FilterValues): InventoryV2FilterParams => {
  const params: InventoryV2FilterParams = {};
  for (const key of INVENTORY_V2_FILTER_KEYS) {
    const value = values[key];
    if (isFilled(value)) {
      params[`filter[${key}]`] = Array.isArray(value) ? value.join(',') : value;
    }
  }
  return params;
};
